import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import {
  CloudDownload as ImportIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { phoneNumberService } from '../services/api';

const steps = [
  'Enter Twilio Credentials',
  'Select Phone Number',
  'Configure Import',
  'Complete Import'
];

const TwilioImportDialog = ({ open, onClose, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Step 1: Credentials
  const [credentials, setCredentials] = useState({
    accountSid: '',
    authToken: ''
  });
  const [credentialsValid, setCredentialsValid] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  
  // Step 2: Phone Numbers
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [filteredNumbers, setFilteredNumbers] = useState([]);
  
  // Step 3: Import Configuration
  const [importConfig, setImportConfig] = useState({
    friendlyName: '',
    monthlyCost: 0,
    notes: ''
  });
  
  // Step 4: Results
  const [importResult, setImportResult] = useState(null);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCredentials({ accountSid: '', authToken: '' });
    setCredentialsValid(false);
    setAccountInfo(null);
    setFilteredNumbers([]);
    setSelectedNumber(null);
    setImportConfig({ friendlyName: '', monthlyCost: 0, notes: '' });
    setImportResult(null);
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Step 1: Test credentials
  const testCredentials = async () => {
    if (!credentials.accountSid || !credentials.authToken) {
      setError('Please enter both Account SID and Auth Token');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await phoneNumberService.testTwilioCredentials(
        credentials.accountSid,
        credentials.authToken
      );

      if (result.success) {
        setCredentialsValid(true);
        setAccountInfo({
          name: result.accountName,
          status: result.accountStatus
        });
        handleNext();
      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to test credentials');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Get phone numbers
  const getPhoneNumbers = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await phoneNumberService.getTwilioPhoneNumbers(
        credentials.accountSid,
        credentials.authToken
      );

      if (result.success) {
        // Filter out numbers already connected to SIP trunks
        const availableNumbers = result.numbers.filter(number => !number.isConnectedToSip);
        setFilteredNumbers(availableNumbers);
        
        if (result.numbers.length === 0) {
          setError('No phone numbers found in your Twilio account');
        } else if (availableNumbers.length === 0) {
          setError('All phone numbers are already connected to SIP trunks. Please disconnect them from existing trunks first.');
        } else {
          handleNext();
        }
      } else {
        setError(result.error || 'Failed to fetch phone numbers');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch phone numbers');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Import phone number
  const importPhoneNumber = async () => {
    if (!selectedNumber || !importConfig.friendlyName) {
      setError('Please select a phone number and enter a friendly name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const importData = {
        accountSid: credentials.accountSid,
        authToken: credentials.authToken,
        phoneNumberSid: selectedNumber.sid,
        friendlyName: importConfig.friendlyName,
        monthlyCost: importConfig.monthlyCost,
        notes: importConfig.notes
      };

      const result = await phoneNumberService.importFromTwilio(importData);

      if (result.success) {
        setImportResult(result);
        handleNext();
        if (onSuccess) {
          onSuccess(result.phoneNumber);
        }
      } else {
        setError(result.error || 'Failed to import phone number');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to import phone number');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Enter Your Twilio Credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We'll use these credentials to access your Twilio account and configure SIP trunking.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account SID"
                  value={credentials.accountSid}
                  onChange={(e) => setCredentials({ ...credentials, accountSid: e.target.value })}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  helperText="Find this in your Twilio Console"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Auth Token"
                  type="password"
                  value={credentials.authToken}
                  onChange={(e) => setCredentials({ ...credentials, authToken: e.target.value })}
                  placeholder="Your Twilio Auth Token"
                  helperText="Find this in your Twilio Console"
                />
              </Grid>
            </Grid>

            {credentialsValid && accountInfo && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <CheckIcon sx={{ mr: 1 }} />
                Connected to: {accountInfo.name} ({accountInfo.status})
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Phone Number to Import
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose a phone number from your Twilio account to import and configure.
            </Typography>

            {filteredNumbers.length > 0 ? (
              <Grid container spacing={2}>
                {filteredNumbers.map((number, index) => (
                  <Grid item xs={12} key={number.sid}>
                    <Card 
                      variant={selectedNumber?.sid === number.sid ? "elevation" : "outlined"}
                      sx={{ 
                        cursor: 'pointer',
                        border: selectedNumber?.sid === number.sid ? 2 : 1,
                        borderColor: selectedNumber?.sid === number.sid ? 'primary.main' : 'divider'
                      }}
                      onClick={() => setSelectedNumber(number)}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="h6" component="div">
                              {number.phoneNumber}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {number.friendlyName || 'No friendly name'}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              {number.capabilities?.voice && <Chip label="Voice" size="small" sx={{ mr: 0.5 }} />}
                              {number.capabilities?.sms && <Chip label="SMS" size="small" sx={{ mr: 0.5 }} />}
                              {number.capabilities?.mms && <Chip label="MMS" size="small" />}
                            </Box>
                            {number.isConnectedToSip && (
                              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                                <Typography variant="caption">
                                  Connected to SIP trunk: {number.trunkSid}
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                          {selectedNumber?.sid === number.sid && (
                            <CheckIcon color="primary" />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">
                No available phone numbers found. All numbers may be connected to existing SIP trunks.
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure Import Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set up how this phone number will appear in your system.
            </Typography>

            {selectedNumber && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <PhoneIcon sx={{ mr: 1 }} />
                Selected: {selectedNumber.phoneNumber}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Friendly Name"
                  value={importConfig.friendlyName}
                  onChange={(e) => setImportConfig({ ...importConfig, friendlyName: e.target.value })}
                  placeholder="e.g., Main Office Line"
                  helperText="How this number will appear in your system"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monthly Cost"
                  type="number"
                  value={importConfig.monthlyCost}
                  onChange={(e) => setImportConfig({ ...importConfig, monthlyCost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  helperText="Optional: Track monthly costs"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={importConfig.notes}
                  onChange={(e) => setImportConfig({ ...importConfig, notes: e.target.value })}
                  placeholder="Optional notes about this phone number"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Import Complete!
            </Typography>
            
            {importResult && (
              <Box>
                <Alert severity="success" sx={{ mb: 3 }}>
                  <CheckIcon sx={{ mr: 1 }} />
                  Phone number imported successfully!
                </Alert>

                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Imported Number
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {importResult.phoneNumber.phoneNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Friendly Name: {importResult.phoneNumber.friendlyName}
                    </Typography>
                  </CardContent>
                </Card>

                {importResult.sipConfiguration && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        SIP Configuration
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Termination URI:</strong> {importResult.sipConfiguration.terminationUri}
                      </Typography>
                      {importResult.sipConfiguration.credentials && (
                        <>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Username:</strong> {importResult.sipConfiguration.credentials.username}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Password:</strong> {importResult.sipConfiguration.credentials.password}
                          </Typography>
                        </>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        These credentials have been saved securely in your database.
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const getStepActions = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            variant="contained"
            onClick={testCredentials}
            disabled={loading || !credentials.accountSid || !credentials.authToken}
            startIcon={loading ? <CircularProgress size={20} /> : <SecurityIcon />}
          >
            {loading ? 'Testing...' : 'Test Credentials'}
          </Button>
        );

      case 1:
        return (
          <Button
            variant="contained"
            onClick={getPhoneNumbers}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PhoneIcon />}
          >
            {loading ? 'Loading...' : 'Load Phone Numbers'}
          </Button>
        );

      case 2:
        return (
          <Button
            variant="contained"
            onClick={importPhoneNumber}
            disabled={loading || !selectedNumber || !importConfig.friendlyName}
            startIcon={loading ? <CircularProgress size={20} /> : <ImportIcon />}
          >
            {loading ? 'Importing...' : 'Import Phone Number'}
          </Button>
        );

      case 3:
        return (
          <Button
            variant="contained"
            onClick={handleClose}
            startIcon={<CheckIcon />}
          >
            Done
          </Button>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ImportIcon />
          Import Phone Number from Twilio
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        {activeStep > 0 && activeStep < 3 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        {getStepActions()}
      </DialogActions>
    </Dialog>
  );
};

export default TwilioImportDialog; 